import {
  ECalculatedFieldFunction,
  ESortType,
  IDatabaseTableModel,
  IGenericSelectCalculatedField,
  IGenericSelectDataModel,
  IGenericSelectSortDataModel
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import * as React from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";

export interface ISortOrderState {}

export interface ISortOrderProps {
  tables: IDatabaseTableModel[];
  selectData: IGenericSelectDataModel;
  updateSelectData: (newData: IGenericSelectDataModel) => void;
  getTypeIcon: (type: string) => React.ReactElement;
  isModal?: boolean;
  enablePagination?: boolean;
}

const sortTypeOptions = [
  { value: ESortType.ascending, label: "Ascending" },
  { value: ESortType.descending, label: "Descending" }
];

class SortOrder extends React.PureComponent<ISortOrderProps, ISortOrderState> {
  private static readonly CALCULATED_FIELDS_TABLE_KEY = "__calculated_fields__";

  constructor(props: ISortOrderProps) {
    super(props);
    this.state = {};
  }

  supportsSort = (func?: ECalculatedFieldFunction): boolean => {
    if (func === ECalculatedFieldFunction.RowNumber) return false;
    if (func === ECalculatedFieldFunction.DateDiff) return true;
    return false;
  };

  getSortableCalculatedFields = (): IGenericSelectCalculatedField[] => {
    return (this.props.selectData.calculatedFields || []).filter(
      (x) => x.alias && x.alias.trim() !== "" && this.supportsSort(x.function)
    );
  };

  isCalculatedFieldsTableSelected = (tableName: string): boolean => {
    return tableName === SortOrder.CALCULATED_FIELDS_TABLE_KEY;
  };

  handleUpdateSortData = (newVal: IGenericSelectSortDataModel, idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    selectData.sortData[idx] = newVal;
    this.props.updateSelectData(selectData);
  };

  handleDeleteSortData = (idx: number) => {
    const selectData = _.cloneDeep(this.props.selectData);
    selectData.sortData.splice(idx, 1);
    this.props.updateSelectData(selectData);
  };

  handleAddOrder = () => {
    const selectData = _.cloneDeep(this.props.selectData);
    selectData.sortData.push({
      tableName: "",
      tableSchema: "",
      colName: "",
      sortType: ESortType.ascending
    });
    this.props.updateSelectData(selectData);
  };

  getSelectedTableColumns = (tableName: string) => {
    const targetTable = this.props.tables.find((x) => x.name === tableName);
    return targetTable?.columns ?? [];
  };

  handleTableSelect = (selectedValue: string, idx: number) => {
    const sortData = _.cloneDeep(this.props.selectData.sortData[idx]);
    const sortDataAny = sortData as any;

    if (selectedValue === SortOrder.CALCULATED_FIELDS_TABLE_KEY) {
      sortData.tableName = SortOrder.CALCULATED_FIELDS_TABLE_KEY;
      sortData.tableAlias = "";
      sortData.tableSchema = "";
      sortData.tableId = undefined;
      sortData.colName = "";
      sortDataAny.isCalculatedField = true;
      sortDataAny.calculatedFieldAlias = "";
      this.handleUpdateSortData(sortData, idx);
      return;
    }

    // Find the selected table by alias or name
    const selectedTable = this.props.selectData.tables.find((x) => (x.alias || x.name) === selectedValue);
    if (selectedTable) {
      sortData.tableName = selectedTable.name;
      sortData.tableAlias = selectedTable.alias;
      sortData.tableSchema = this.props.tables.find((x) => x.name === selectedTable.name)?.schema ?? "";
      sortData.tableId = this.props.tables.find((x) => x.name === selectedTable.name)?.id;
    }
    sortData.colName = "";
    sortDataAny.isCalculatedField = false;
    sortDataAny.calculatedFieldAlias = "";
    this.handleUpdateSortData(sortData, idx);
  };

  handleColumnSelect = (colName: string, idx: number) => {
    const sortData = _.cloneDeep(this.props.selectData.sortData[idx]);
    const sortDataAny = sortData as any;

    if (this.isCalculatedFieldsTableSelected(sortData.tableName)) {
      const calcField = this.getSortableCalculatedFields().find((x) => x.alias === colName);
      if (calcField) {
        sortData.tableName = SortOrder.CALCULATED_FIELDS_TABLE_KEY;
        sortData.tableAlias = "";
        sortData.tableSchema = "";
        sortData.tableId = undefined;
        sortData.colName = calcField.alias;
        sortDataAny.isCalculatedField = true;
        sortDataAny.calculatedFieldAlias = calcField.alias;
        this.handleUpdateSortData(sortData, idx);
        return;
      }
    }

    sortData.colName = colName;
    sortDataAny.isCalculatedField = false;
    sortDataAny.calculatedFieldAlias = "";
    this.handleUpdateSortData(sortData, idx);
  };

  handleSortTypeSelect = (sortType: ESortType, idx: number) => {
    const sortData = _.cloneDeep(this.props.selectData.sortData[idx]);
    sortData.sortType = sortType;
    this.handleUpdateSortData(sortData, idx);
  };

  getValidationSuffixIcon = (hasError: boolean, hasValue: boolean) => {
    if (hasError && !hasValue) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />
          <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} color="rgba(96, 111, 133, 1)" />
        </div>
      );
    }
    return undefined;
  };

  getColumns = (): KuikaTableColumn<IGenericSelectSortDataModel & { _index: number }>[] => {
    const sortableCalculatedFields = this.getSortableCalculatedFields();

    return [
      {
        id: "tableName",
        header: () => "Table",
        cellClassName: (row) => (row.validations?.tableName === false ? "has-validation-error" : ""),
        cell: (row, tableRow) => {
          const tableOptions = this.props.selectData.tables.map((x) => ({
            value: x.alias || x.name,
            label: (
              <>
                <KuikaSvg name="table" width={12} height={12} color="var(--dark-5)" />
                {x.alias || x.name}
              </>
            )
          }));

          // Add calculated fields option if there are sortable calculated fields
          if (sortableCalculatedFields.length > 0) {
            tableOptions.push({
              value: SortOrder.CALCULATED_FIELDS_TABLE_KEY,
              label: (
                <>
                  <KuikaSvg name="function" width={12} height={12} color="var(--dark-5)" />
                  Calculated Fields
                </>
              )
            });
          }

          const hasValidationError = row.validations?.tableName === false;
          return (
            <div style={{ width: "100%" }}>
              <KuikaSelectbox
                className="kuika-selectbox--no-border"
                searchable
                value={row.tableName}
                onChange={(val) => this.handleTableSelect(val, row._index)}
                options={tableOptions}
                placeholder="Select table"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!row.tableName)}
              />
            </div>
          );
        }
      },
      {
        id: "colName",
        header: () => "Column",
        cellClassName: (row) => (row.validations?.colName === false ? "has-validation-error" : ""),
        cell: (row) => {
          // Column options - show calculated field aliases if calculated fields table is selected
          const columnOptions = this.isCalculatedFieldsTableSelected(row.tableName)
            ? sortableCalculatedFields.map((x) => {
                const iconName = x.function === ECalculatedFieldFunction.DateDiff ? "calendar-event" : "layout-rows";
                const funcName = x.function === ECalculatedFieldFunction.DateDiff ? "DateDiff" : "RowNumber";
                return {
                  value: x.alias,
                  label: (
                    <>
                      <KuikaSvg name={iconName} width={12} height={12} color="var(--dark-5)" />
                      {funcName}: {x.alias}
                    </>
                  ),
                  searchLabel: `${funcName} ${x.alias}`
                };
              })
            : this.getSelectedTableColumns(row.tableName).map((x) => ({
                value: x.name,
                label: (
                  <>
                    {this.props.getTypeIcon(x.kuikaDataType)}
                    {x.name}
                  </>
                )
              }));

          const hasValidationError = row.validations?.colName === false;
          return (
            <div style={{ width: "100%" }}>
              <KuikaSelectbox
                className="kuika-selectbox--no-border"
                searchable
                value={row.colName}
                onChange={(val) => this.handleColumnSelect(val, row._index)}
                options={columnOptions}
                placeholder="Select column"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!row.colName)}
              />
            </div>
          );
        }
      },
      {
        id: "sortType",
        header: () => "Sort Type",
        cellClassName: (row) => (row.validations?.sortType === false ? "has-validation-error" : ""),
        cell: (row) => {
          const hasValidationError = row.validations?.sortType === false;
          return (
            <div style={{ width: "100%" }}>
              <KuikaSelectbox
                className="kuika-selectbox--no-border"
                searchable
                value={row.sortType}
                onChange={(val) => this.handleSortTypeSelect(val, row._index)}
                options={sortTypeOptions}
                placeholder="Select sort type"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!row.sortType)}
              />
            </div>
          );
        },
        width: 140
      },
      {
        id: "actions",
        header: () => "",
        cell: (row) => (
          <KuikaSvg
            name="trash"
            width={16}
            height={16}
            color="var(--red-5)"
            pointer
            onClick={() => this.handleDeleteSortData(row._index)}
          />
        ),
        width: 36,
        align: "center",
        noWrap: true
      }
    ];
  };

  render() {
    const dataWithIndex = this.props.selectData.sortData.map((sortData, idx) => ({
      ...sortData,
      _index: idx
    }));

    const showPaginationWarning =
      this.props.enablePagination && (!this.props.selectData.sortData || this.props.selectData.sortData.length === 0);

    return (
      <>
        {showPaginationWarning && (
          <KuikaInfoBox
            color="yellow"
            leftUnit={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <KuikaSvg name="circle-warning" width={16} height={16} />
                <KuikaSmallText>
                  Sort order is required when pagination is enabled. Please add at least one sort field.
                </KuikaSmallText>
              </div>
            }
          />
        )}
        <KuikaTable
          data={dataWithIndex}
          columns={this.getColumns()}
          rowKey={(row) => `sort-${row._index}`}
          enablePagination={false}
          emptyTitle="No sort order"
          emptyDescription="Add a sort order to order results"
        />
        <div className="generic-sql__filter__buttons">
          <KuikaButton type="text" size="sm" onClick={this.handleAddOrder}>
            + ADD ORDER
          </KuikaButton>
        </div>
      </>
    );
  }
}

export const SortOrderComponent = SortOrder;
